import * as React from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { useTheme } from '../../theme';
import Money from '../../Money/components/Money';
import { MoneyType } from '../../PriceMatrix/types';
import { default as BaseButton } from '../../BaseComponents/Button';

export interface Props {
	className?: string;
	headerClassName?: string;
	textClassName?: string;
	contentClassName?: string;
	wrapperClassName?: string;
	footerClassName?: string;
	header?: React.ReactNode | string;
	subheader?: React.ReactNode | string;
	text?: React.ReactNode | string;
	totalPrice?: MoneyType;
	isLoading?: boolean;
	isConfirmDisabled?: boolean;
	onConfirm?: () => void;
	onCancel?: () => void;
	showClear?: boolean;
	readOnly?: boolean;
	onClear?: () => void;
	submit?: boolean;
}

const AdditionalServiceWrapper: React.FC<Props> = ({
	className,
	headerClassName,
	textClassName,
	contentClassName,
	wrapperClassName,
	footerClassName,
	children,
	header,
	subheader,
	text,
	totalPrice,
	isLoading,
	isConfirmDisabled,
	onConfirm,
	onCancel,
	onClear,
	showClear = true,
	readOnly,
	submit
}) => {
	const { AdditionalServiceWrapper: css } = useTheme('AdditionalServiceWrapper');
	const { t } = useTranslation('AdditionalServiceWrapper');

	return (
		<div className={cn(className, css.wrapper)}>
			<div className={cn(css.header, headerClassName)}>
				{header}
				{subheader && <div className={css.subheader}>{subheader}</div>}
			</div>

			<div className={cn(css.content, contentClassName)}>
				{text && <div className={cn(textClassName, css.content_text)}>{text}</div>}
				<div className={cn(wrapperClassName, css.content_wrapper)}>{children}</div>
			</div>

			<div className={cn(footerClassName, css.footer)}>
				{totalPrice && (
					<div className={css.total}>
						<span className={cn(css.total__price, { [css.total__price_withBorder]: showClear })}>
							{t('Total')}:
							<Money money={totalPrice} />
						</span>
						{showClear && (
							<BaseButton
								className={css.total__clear}
								disabled={isLoading}
								variant="text"
								onClick={onClear}
							>
								{t('Clear')}
							</BaseButton>
						)}
					</div>
				)}

				<div className={css.controls}>
					{!isLoading && onCancel && (
						<BaseButton variant="secondary" className={cn(css.button, css.cancel)} onClick={onCancel}>
							{t('Cancel')}
						</BaseButton>
					)}

					{!readOnly && (
						<BaseButton
							variant="action"
							className={css.button}
							classes={{
								disabled: css.button_disabled
							}}
							onClick={!submit ? onConfirm : null}
							isLoading={isLoading}
							disabled={isConfirmDisabled || isLoading}
							type={submit ? 'submit' : 'button'}
						>
							{t('Confirm')}
						</BaseButton>
					)}
				</div>
			</div>
		</div>
	);
};

export default AdditionalServiceWrapper;
